import { useState, useEffect } from 'react';
import { useAuthStore } from '@/lib/store';
import { api } from '@/lib/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { TelegramAuthData } from '@/types';

const TELEGRAM_BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'LazaiTraderBot';

export default function SettingsPage() {
  const { user, setAuth, sessionToken } = useAuthStore();
  const [isLinkingTelegram, setIsLinkingTelegram] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const handleLinkTelegram = async (telegramData: TelegramAuthData) => {
    setIsLinkingTelegram(true);
    setLinkError(null);
    setLinkSuccess(false);

    try {
      const { user: updatedUser } = await api.linkTelegram(telegramData);
      if (sessionToken) {
        setAuth(updatedUser, sessionToken);
      }
      setLinkSuccess(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link Telegram';
      setLinkError(message);
    } finally {
      setIsLinkingTelegram(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-dark-400">Manage your account settings</p>
      </div>

      {/* Account Info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <label className="label">User ID</label>
            <p className="text-white">{user?.userId}</p>
          </div>

          <div>
            <label className="label">Registered</label>
            <p className="text-white">
              {user?.registeredAt
                ? new Date(user.registeredAt).toLocaleDateString()
                : 'Unknown'}
            </p>
          </div>

          <div>
            <label className="label">Registration Method</label>
            <p className="text-white capitalize">{user?.registeredVia || 'Unknown'}</p>
          </div>
        </div>
      </div>

      {/* Wallet */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Wallet</h2>
        {user?.walletAddress ? (
          <div className="space-y-4">
            <div>
              <label className="label">Connected Wallet</label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 bg-dark-900 rounded-lg text-primary-400 font-mono text-sm break-all">
                  {user.walletAddress}
                </code>
                <button
                  onClick={() => copyToClipboard(user.walletAddress!)}
                  className="btn btn-secondary p-2"
                  title="Copy"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            {user.scwAddress && (
              <div>
                <label className="label">Smart Contract Wallet (SCW)</label>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 px-3 py-2 bg-dark-900 rounded-lg text-green-400 font-mono text-sm break-all">
                    {user.scwAddress}
                  </code>
                  <button
                    onClick={() => copyToClipboard(user.scwAddress!)}
                    className="btn btn-secondary p-2"
                    title="Copy"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-dark-400 mt-2">
                  Deposit funds to this address to start trading
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-dark-400 mb-4">No wallet connected</p>
            <p className="text-sm text-dark-500">
              Connect a wallet through the login page to link it to your account
            </p>
          </div>
        )}
      </div>

      {/* Telegram */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Telegram</h2>
        {user?.telegramUsername || user?.telegramUserId ? (
          <div className="space-y-2">
            {user.telegramUsername && (
              <div>
                <label className="label">Username</label>
                <p className="text-white">@{user.telegramUsername}</p>
              </div>
            )}
            {user.telegramUserId && (
              <div>
                <label className="label">Telegram ID</label>
                <p className="text-white font-mono">{user.telegramUserId}</p>
              </div>
            )}
            <p className="text-sm text-green-400 mt-2">
              ✓ Telegram account linked
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {linkError && (
              <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">
                {linkError}
              </div>
            )}

            {linkSuccess ? (
              <div className="bg-green-900/50 border border-green-700 rounded-lg p-3 text-green-200 text-sm">
                Telegram account linked successfully!
              </div>
            ) : (
              <>
                <p className="text-dark-400">
                  Link your Telegram account to receive notifications and manage your
                  strategies via the Telegram bot.
                </p>

                {isLinkingTelegram ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner />
                    <span className="ml-2 text-dark-300">Linking Telegram...</span>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <TelegramLoginWidget
                      botUsername={TELEGRAM_BOT_USERNAME}
                      onAuth={handleLinkTelegram}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="card border-red-900/50">
        <h2 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h2>
        <p className="text-dark-400 text-sm mb-4">
          These actions are irreversible. Please proceed with caution.
        </p>
        <button className="btn btn-danger" disabled>
          Delete Account
        </button>
        <p className="text-xs text-dark-500 mt-2">
          Account deletion is not available yet. Please contact support.
        </p>
      </div>
    </div>
  );
}

// Simple inline Telegram widget for linking
function TelegramLoginWidget({
  botUsername,
  onAuth,
}: {
  botUsername: string;
  onAuth: (data: TelegramAuthData) => void;
}) {
  useEffect(() => {
    (window as unknown as { TelegramLoginWidgetSettings?: { dataOnauth: (user: TelegramAuthData) => void } }).TelegramLoginWidgetSettings = {
      dataOnauth: onAuth,
    };
  }, [onAuth]);

  return (
    <div
      ref={(el) => {
        if (el && !el.hasChildNodes()) {
          const script = document.createElement('script');
          script.src = 'https://telegram.org/js/telegram-widget.js?22';
          script.setAttribute('data-telegram-login', botUsername);
          script.setAttribute('data-size', 'medium');
          script.setAttribute('data-radius', '8');
          script.setAttribute(
            'data-onauth',
            'TelegramLoginWidgetSettings.dataOnauth(user)'
          );
          script.async = true;
          el.appendChild(script);
        }
      }}
    />
  );
}
